import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import { useCommonStore } from '@/stores/CommonStore'
import { loadLocales } from '@/utils/i18n.utils'
import { PROFESSIONALS_TRACKING_NAME } from './professionals.constants'

const AccountSettingsProfessionals = () =>
  import('@/pages/accounts/AccountSettingsProfessionals.vue')
const ProfessionalDetails = () =>
  import('@/pages/accounts/ProfessionalDetails.vue')
const ProfessionalsHistory = () =>
  import('@/pages/accounts/ProfessionalsHistory.vue')

export async function loadProfessionalsLocales(): Promise<void> {
  await loadLocales('professionals', 'postMatch')
}

export async function resolveProfessionalsRouteGuard(): Promise<
  true | { name: 'generalInfo' }
> {
  const { globalFlagsLoaded, featureFlags } = storeToRefs(useCommonStore())

  if (!globalFlagsLoaded.value) {
    await new Promise<void>((resolve) => {
      const unwatch = watch(globalFlagsLoaded, (loaded) => {
        if (loaded) {
          unwatch()
          resolve()
        }
      })

      if (globalFlagsLoaded.value) {
        unwatch()
        resolve()
      }
    })
  }

  if (featureFlags.value?.web_third_party_access) {
    return true
  }

  return { name: 'generalInfo' }
}

async function professionalsBeforeEnter(): Promise<
  true | { name: 'generalInfo' }
> {
  const guardResult = await resolveProfessionalsRouteGuard()

  if (guardResult !== true) {
    return guardResult
  }

  await loadProfessionalsLocales()

  return true
}

/**
 * Professionals routes rendered as tabs within the account settings shell
 * (`accountSettings` layout with the shared tab navigation).
 */
export function getProfessionalsTabRoutes(): RouteRecordRaw[] {
  return [
    {
      name: 'professionals',
      path: '/accountSettings/professionals',
      component: AccountSettingsProfessionals,
      beforeEnter: professionalsBeforeEnter,
      meta: { requiresAuth: true, trackingName: PROFESSIONALS_TRACKING_NAME }
    },
    {
      name: 'professionalsHistory',
      path: '/accountSettings/professionals/history',
      component: ProfessionalsHistory,
      beforeEnter: professionalsBeforeEnter,
      meta: { requiresAuth: true, trackingName: PROFESSIONALS_TRACKING_NAME }
    }
  ]
}

/**
 * Professionals detail routes rendered full-page under `accountSettingsFull`,
 * outside of the tabbed account settings shell.
 */
export function getProfessionalsDetailRoutes(): RouteRecordRaw[] {
  return [
    {
      name: 'professionalInvitationDetail',
      path: '/accountSettings/professionals/invitations/:invitationId',
      component: ProfessionalDetails,
      beforeEnter: professionalsBeforeEnter,
      meta: { requiresAuth: true, trackingName: PROFESSIONALS_TRACKING_NAME }
    },
    {
      name: 'professionalMatchDetail',
      path: '/accountSettings/professionals/matches/:relationshipId',
      component: ProfessionalDetails,
      beforeEnter: professionalsBeforeEnter,
      meta: { requiresAuth: true, trackingName: PROFESSIONALS_TRACKING_NAME }
    }
  ]
}
